import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

/**
 * What this user may do in administration.
 *
 * Every code below comes from `src/lib/permissions.ts`, generated from the server's own catalogue —
 * unlike `customers/permissions.ts`, nothing here has to be hand-typed, because the Access module
 * already publishes a constant for every action this pass builds.
 *
 * Presentation only, as everywhere else: the server re-checks every command against the live
 * catalogue, so a hidden control is not a closed door and a visible one is not an open door.
 *
 * Two things are precise on purpose rather than convenient:
 *
 * - `userLockApprove` and `userDisableApprove` do not exist. Of the three status transitions, only
 *   *unlocking* has a countersigning code in the catalogue — `admin.user.unlock.approve` — so
 *   locking and disabling an account are plain actions here, never wrapped in a dual-control
 *   notice, while unlocking is. `useAdminPermissions` states this rather than leaving it implicit.
 * - Issuing a temporary password has no permission code of its own. The button is gated on
 *   `userUpdate`, the closest generated code over an account's own maintenance, and the comment on
 *   `issueTemporaryPassword` in `admin-api.ts` says the same thing the server is the actual
 *   authority on it either way.
 */
export const ADMIN_PERMISSIONS = {
  accessReview: PERMISSIONS.admin.accessReview,
  allBranches: PERMISSIONS.admin.allBranches,
  approvalPolicyManage: PERMISSIONS.admin.approvalPolicyManage,
  approvalPolicyManageApprove: PERMISSIONS.admin.approvalPolicyManageApprove,
  branchManage: PERMISSIONS.admin.branchManage,
  branchView: PERMISSIONS.admin.branchView,
  holidayManage: PERMISSIONS.admin.holidayManage,
  mfaReset: PERMISSIONS.admin.mfaReset,
  mfaResetApprove: PERMISSIONS.admin.mfaResetApprove,
  parameterManage: PERMISSIONS.admin.parameterManage,
  parameterView: PERMISSIONS.admin.parameterView,
  permissionView: PERMISSIONS.admin.permissionView,
  roleAssign: PERMISSIONS.admin.roleAssign,
  roleAssignApprove: PERMISSIONS.admin.roleAssignApprove,
  roleManage: PERMISSIONS.admin.roleManage,
  roleManageApprove: PERMISSIONS.admin.roleManageApprove,
  roleView: PERMISSIONS.admin.roleView,
  sodOverride: PERMISSIONS.admin.sodOverride,
  userCreate: PERMISSIONS.admin.userCreate,
  userCreateApprove: PERMISSIONS.admin.userCreateApprove,
  userDisable: PERMISSIONS.admin.userDisable,
  userLock: PERMISSIONS.admin.userLock,
  userUnlock: PERMISSIONS.admin.userUnlock,
  userUnlockApprove: PERMISSIONS.admin.userUnlockApprove,
  userUpdate: PERMISSIONS.admin.userUpdate,
  userUpdateApprove: PERMISSIONS.admin.userUpdateApprove,
  userView: PERMISSIONS.admin.userView,
  // Not this module's own code: gates revoking a field device from a user's record — see
  // `admin-api.ts`'s note on why that call is not treated as dual-controlled despite this
  // permission having a `.approve` sibling in the catalogue.
  deviceRevoke: PERMISSIONS.sync.deviceRevoke,
} as const satisfies Record<string, PermissionCode>

/** Any one of these is enough to have business in administration at all. */
export const ADMIN_MODULE_PERMISSIONS: readonly PermissionCode[] = [
  ADMIN_PERMISSIONS.userView,
  ADMIN_PERMISSIONS.roleView,
  ADMIN_PERMISSIONS.branchView,
]

export interface AdminPermissions {
  canEnterModule: boolean
  canViewUsers: boolean
  canCreateUser: boolean
  canCountersignCreateUser: boolean
  canUpdateUser: boolean
  canCountersignUpdateUser: boolean
  canLockUser: boolean
  canUnlockUser: boolean
  canCountersignUnlockUser: boolean
  canDisableUser: boolean
  canResetMfa: boolean
  canCountersignResetMfa: boolean
  canAssignRoles: boolean
  canCountersignAssignRoles: boolean
  canViewRoles: boolean
  canManageRolePermissions: boolean
  canCountersignRolePermissions: boolean
  canViewPermissionCatalogue: boolean
  canViewBranches: boolean
  canRevokeDevice: boolean
}

export function useAdminPermissions(): AdminPermissions {
  const { hasPermission, hasAnyPermission } = useAuth()

  return useMemo(
    () => ({
      canEnterModule: hasAnyPermission(ADMIN_MODULE_PERMISSIONS),
      canViewUsers: hasPermission(ADMIN_PERMISSIONS.userView),
      canCreateUser: hasPermission(ADMIN_PERMISSIONS.userCreate),
      canCountersignCreateUser: hasPermission(ADMIN_PERMISSIONS.userCreateApprove),
      canUpdateUser: hasPermission(ADMIN_PERMISSIONS.userUpdate),
      canCountersignUpdateUser: hasPermission(ADMIN_PERMISSIONS.userUpdateApprove),
      canLockUser: hasPermission(ADMIN_PERMISSIONS.userLock),
      canUnlockUser: hasPermission(ADMIN_PERMISSIONS.userUnlock),
      canCountersignUnlockUser: hasPermission(ADMIN_PERMISSIONS.userUnlockApprove),
      canDisableUser: hasPermission(ADMIN_PERMISSIONS.userDisable),
      canResetMfa: hasPermission(ADMIN_PERMISSIONS.mfaReset),
      canCountersignResetMfa: hasPermission(ADMIN_PERMISSIONS.mfaResetApprove),
      canAssignRoles: hasPermission(ADMIN_PERMISSIONS.roleAssign),
      canCountersignAssignRoles: hasPermission(ADMIN_PERMISSIONS.roleAssignApprove),
      canViewRoles: hasPermission(ADMIN_PERMISSIONS.roleView),
      canManageRolePermissions: hasPermission(ADMIN_PERMISSIONS.roleManage),
      canCountersignRolePermissions: hasPermission(ADMIN_PERMISSIONS.roleManageApprove),
      canViewPermissionCatalogue: hasPermission(ADMIN_PERMISSIONS.permissionView),
      canViewBranches: hasPermission(ADMIN_PERMISSIONS.branchView),
      canRevokeDevice: hasPermission(ADMIN_PERMISSIONS.deviceRevoke),
    }),
    [hasPermission, hasAnyPermission],
  )
}
